import React from 'react';

import { translations } from '../translations/en';

interface EarResultGraphProps {
  earSide: 'left' | 'right';
  averageScore: number;
  severity: 'good' | 'moderate' | 'severe';
  goodMaxScore?: number;
  moderateMaxScore?: number;
}

export const EarResultGraph: React.FC<EarResultGraphProps> = ({
  earSide,
  averageScore,
  severity,
  goodMaxScore = 4,
  moderateMaxScore = 8,
}) => {
  const normalizeScore = (score: number): number => {
    const min = 1;
    const max = 12;
    return (score - min) / (max - min);
  };
  const progressWidth = 100 - normalizeScore(averageScore) * 100;
  const goodThresholdPosition = 100 - normalizeScore(goodMaxScore) * 100;
  const moderateThresholdPosition = 100 - normalizeScore(moderateMaxScore) * 100;
  const moderateMiddlePosition = (moderateThresholdPosition + goodThresholdPosition) / 2;

  const getEarDescription = (): string => {
    if (severity === 'good') return translations.resultgooddesc;
    if (severity === 'moderate') return translations.resultmoderatedesc;
    return translations.resultseveredesc;
  };

  const earLabel = earSide === 'left' ? translations.leftear : translations.rightear;

  return (
    <div className="w-full rounded-lg border px-6 py-14 shadow-md">
      <div className="mb-4 pt-8">
        <div className="relative flex h-[50px] rounded-[50px] border border-black">
          <div className="absolute left-0 top-0 h-full w-full overflow-hidden rounded-[50px]">
            <div
              className="absolute left-0 top-0 h-full bg-[#545859] transition-all duration-500"
              style={{ width: `${progressWidth}%` }}
            />
          </div>
          <div
            className="absolute top-0 h-full w-[2px] bg-black opacity-30"
            style={{ left: `${moderateThresholdPosition}%` }}
          />
          <div
            className="absolute top-0 h-full w-[2px] bg-black opacity-30"
            style={{ left: `${goodThresholdPosition}%` }}
          />
          <div className="absolute bottom-full mb-2 w-full">
            <span className="absolute bottom-0 left-0 text-sm max-xs:w-12">
              {translations.resultsignificantloss}
            </span>
            <span
              className="absolute bottom-0 -translate-x-1/2 text-sm"
              style={{ left: `${moderateMiddlePosition}%` }}
            >
              {translations.resultloss}
            </span>
            <span className="absolute bottom-0 right-0 text-sm">{translations.resultgood}</span>
          </div>
        </div>
      </div>
      <div className="text-center">
        <h4 className="mb-3 text-2xl font-bold">{earLabel}</h4>
        <div className="text-gray-700">{getEarDescription()}</div>
      </div>
    </div>
  );
};
